import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Users, Building, Briefcase, TrendingUp } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const ClientDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeRFQs: 0,
    totalCandidates: 0,
    pendingApplications: 0,
  });
  const [clientName, setClientName] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) return;

      // Get client contact info
      const { data: contact } = await supabase
        .from('client_contacts')
        .select('*, client:clients(name)')
        .eq('user_id', user.id)
        .single();

      if (contact?.client) {
        setClientName(contact.client.name);

        // Get RFQs count
        const { count: rfqCount } = await supabase
          .from('rfqs')
          .select('*', { count: 'exact', head: true })
          .eq('client_id', contact.client_id)
          .eq('status', 'Open');

        // Get total candidates count (all active candidates)
        const { count: candidateCount } = await supabase
          .from('candidates')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'Active');

        // Get applications count for this client's RFQs
        const { count: appCount } = await supabase
          .from('applications')
          .select('rfq:rfqs!inner(client_id)', { count: 'exact', head: true })
          .eq('rfq.client_id', contact.client_id)
          .eq('status', 'Reviewing');

        setStats({
          activeRFQs: rfqCount || 0,
          totalCandidates: candidateCount || 0,
          pendingApplications: appCount || 0,
        });
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold">Welcome back!</h2>
        {clientName && <p className="text-muted-foreground mt-1 text-sm sm:text-base">{clientName}</p>}
      </div>

      {/* Stats Grid */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active RFQs</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeRFQs}</div>
            <p className="text-xs text-muted-foreground">Open job requests</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Available Candidates</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalCandidates}</div>
            <p className="text-xs text-muted-foreground">Active candidates in pool</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Reviews</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.pendingApplications}</div>
            <p className="text-xs text-muted-foreground">Applications to review</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks and information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <a
              href="/client/candidates"
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent hover:shadow-md transition-all cursor-pointer"
            >
              <div>
                <h3 className="font-medium">Search Candidates</h3>
                <p className="text-sm text-muted-foreground">Browse available candidates</p>
              </div>
              <span className="text-primary">View →</span>
            </a>
            <a
              href="/client/my-details"
              className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent hover:shadow-md transition-all cursor-pointer"
            >
              <div>
                <h3 className="font-medium">Update My Details</h3>
                <p className="text-sm text-muted-foreground">Manage your contact information</p>
              </div>
              <span className="text-primary">Edit →</span>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDashboard;
